import React, { useState } from 'react'
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  XCircle,
  Globe,
  Users,
  BarChart3,
  Shield,
  ArrowRight,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import { validateRegister, LIMITS } from '../validation'
import SchoolTermsAcceptance from '../components/SchoolTermsAcceptance'

interface RegisterForm {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phone: string
}

const BENEFITS = [
  { icon: Globe, title: 'Global Visibility', desc: 'Reach students worldwide searching for yoga teacher training' },
  { icon: Users, title: 'Easy Booking Management', desc: 'Handle inquiries, bookings, and schedules in one place' },
  { icon: BarChart3, title: 'Insights & Analytics', desc: 'Track performance, reviews, and revenue growth' },
  { icon: Shield, title: 'Verified Trust Badge', desc: 'Build credibility with our verification and review system' },
]

const Register: React.FC = () => {
  const location = useLocation()
  const prefillEmail = (location.state as { email?: string } | null)?.email || ''
  const [formData, setFormData] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    email: prefillEmail,
    password: '',
    confirmPassword: '',
    phone: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [claimRedirectMessage, setClaimRedirectMessage] = useState<string | null>(null)
  /**
   * When the backend replies with `canClaim: true` (profile was pre-onboarded
   * by an admin and hasn't been verified yet), we surface an inline "Claim
   * profile" CTA under the email field instead of letting the user retry in
   * a loop. Stored as the email so the claim page can pre-fill it.
   */
  const [claimEmail, setClaimEmail] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState<string | null>(null)
  const { user } = useAuth()
  const navigate = useNavigate()

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  const validateForm = (): boolean => {
    const newErrors = validateRegister(formData)
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const getPasswordStrength = (password: string) => {
    const checks = {
      length: password.length >= LIMITS.password.min,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
    }
    const passedChecks = Object.values(checks).filter(Boolean).length
    let strength: 'weak' | 'medium' | 'strong' = 'weak'
    if (passedChecks >= 4) strength = 'strong'
    else if (passedChecks >= 3) strength = 'medium'
    return { strength, checks }
  }

  const passwordStrength = formData.password ? getPasswordStrength(formData.password) : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!termsAccepted) {
      setTermsError('You must accept the Terms of Service and Privacy Policy to create an account.')
      return
    }
    setTermsError(null)

    setIsLoading(true)
    setErrors({})
    setClaimEmail(null)
    setClaimRedirectMessage(null)

    try {
      const response = await apiService.registerYogaSchoolOwner({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone: formData.phone.trim() || undefined,
        acceptedTerms: true,
      })

      if (response.success) {
        toast.success('Registration successful! Check your email for the 6-digit verification code.')
        setTimeout(() => {
          navigate('/verify-email', {
            replace: true,
            state: { email: formData.email.trim().toLowerCase() },
          })
        }, 500)
      } else {
        throw new Error(response.message || 'Registration failed')
      }
    } catch (error: any) {
      const errData = error.response?.data
      const errObj = errData?.error
      let errorMessage = (typeof errObj === 'object' && errObj?.message) || errObj || errData?.message || error.message || 'Registration failed. Please try again.'
      errorMessage = String(errorMessage)

      if (error.response?.status === 429) {
        const retryAfter = errData?.retryAfter
        errorMessage = retryAfter
          ? `Too many attempts. Please wait ${retryAfter} seconds.`
          : 'Too many attempts. Please wait a few minutes.'
      }

      // Admin-pre-onboarded profile → surface the claim CTA under the email.
      if (error.response?.status === 409 && errData?.canClaim) {
        const claimTargetEmail =
          errData?.email || formData.email.trim().toLowerCase() || null

        setClaimEmail(claimTargetEmail)
        setClaimRedirectMessage(
          'This email already has a pre-created school profile. Redirecting you to Claim Profile...',
        )
        toast.success('Profile found. Continuing to Claim Profile...')

        if (claimTargetEmail) {
          setTimeout(() => {
            navigate(`/claim-profile?email=${encodeURIComponent(claimTargetEmail)}`)
          }, 700)
        }
        return
      }

      toast.error(errorMessage)

      if (errData?.errors && typeof errData.errors === 'object') {
        setErrors(errData.errors)
      } else if (errorMessage.toLowerCase().includes('email')) {
        setErrors({ email: errorMessage })
      } else if (error.response?.status === 429) {
        setErrors({ _general: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' })
    }
  }

  const inputClass = (field: string) =>
    `w-full px-4 py-2.5 border ${
      errors[field] ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white'
    } rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all`

  return (
    <div className="min-h-screen flex bg-primary-50">
      {/* Left Panel — Brand / Benefits (light + orange theme) */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-primary-200/70 via-primary-100 to-white text-gray-900 flex-col justify-between p-12 overflow-hidden border-r border-primary-200/50">
        {/* Decorative circles */}
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-primary-300/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl" />

        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-12 w-auto mb-16" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl font-bold leading-tight tracking-tight text-gray-900"
          >
            Grow your yoga school
            <br />
            <span className="text-primary-600">with the right platform.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-gray-600 text-base leading-relaxed max-w-md"
          >
            Connect with students, manage bookings, and scale your programs—all in one place.
          </motion.p>
        </div>

        <div className="relative z-10 space-y-5 mt-12">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
              className="flex items-start gap-4"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-200/60 border border-primary-300/50 flex items-center justify-center">
                <b.icon className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{b.title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="relative z-10 text-xs text-gray-500 mt-12"
        >
          Built for yoga schools worldwide
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="relative z-10 text-xs text-gray-500 mt-2"
        >
          Contact us: <a href="mailto:info@bookmyyogattc.com" className="text-primary-600 hover:underline">info@bookmyyogattc.com</a>
        </motion.p>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-12 bg-white">
        <div className="w-full max-w-[440px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-10 w-auto" />
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Create your account</h2>
            <p className="mt-1.5 text-sm text-gray-500">
              Start managing your yoga school in minutes.
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Please fill in your personal details.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 space-y-5"
            onSubmit={handleSubmit}
          >
            {errors._general && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {errors._general}
              </div>
            )}
            {claimRedirectMessage && (
              <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900 font-medium">
                {claimRedirectMessage}
              </div>
            )}

            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-xs font-medium text-gray-600 mb-1.5">
                  First name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className={inputClass('firstName')}
                  placeholder="John"
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-xs font-medium text-gray-600 mb-1.5">
                  Last name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className={inputClass('lastName')}
                  placeholder="Doe"
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={inputClass('email')}
                placeholder="you@yourschool.com"
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              {claimEmail && (
                <div className="mt-2 p-3 rounded-xl border border-primary-200 bg-primary-50/60">
                  <p className="text-xs text-gray-700">
                    This profile was set up by our team. You can verify your email
                    and set a password to take it over.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/claim-profile?email=${encodeURIComponent(claimEmail)}`
                      )
                    }
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800 hover:underline"
                  >
                    Claim profile
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-xs font-medium text-gray-600 mb-1.5">
                Phone number <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                className={inputClass('phone')}
                placeholder="+91 98765 43210"
              />
              {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClass('password') + ' pr-10'}
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
              {formData.password && passwordStrength && (
                <div className="mt-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          passwordStrength.strength === 'strong'
                            ? 'bg-green-500 w-full'
                            : passwordStrength.strength === 'medium'
                            ? 'bg-amber-500 w-2/3'
                            : 'bg-red-400 w-1/3'
                        }`}
                      />
                    </div>
                    <span className="text-[11px] font-medium text-gray-500 capitalize w-12">
                      {passwordStrength.strength}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {[
                      { key: 'length', label: '8+ characters' },
                      { key: 'uppercase', label: 'Uppercase' },
                      { key: 'lowercase', label: 'Lowercase' },
                      { key: 'number', label: 'Number' },
                      { key: 'special', label: 'Special char' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center gap-1.5">
                        {passwordStrength.checks[item.key as keyof typeof passwordStrength.checks] ? (
                          <CheckCircle className="h-3 w-3 text-primary-600" />
                        ) : (
                          <XCircle className="h-3 w-3 text-gray-300" />
                        )}
                        <span className="text-[11px] text-gray-500">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={inputClass('confirmPassword') + ' pr-10'}
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>}
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="mt-1 text-xs text-primary-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-primary-600" />
                  Passwords match
                </p>
              )}
            </div>

            <SchoolTermsAcceptance
              idPrefix="register"
              accepted={termsAccepted}
              onAcceptedChange={(v) => {
                setTermsAccepted(v)
                if (v) setTermsError(null)
              }}
              error={termsError || undefined}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || !termsAccepted}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-500 pt-2">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                Sign in
              </Link>
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  )
}

export default Register
